/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  GetAdminOpenRouterKeyUsageRequest,
  GetAdminOpenRouterKeyUsageSecurity,
  KeyType,
} from "../models/operations/getadminopenrouterkeyusage.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  AdminOpenRouterKeyUsageQueryData,
  buildAdminOpenRouterKeyUsageQuery,
  prefetchAdminOpenRouterKeyUsage,
  queryKeyAdminOpenRouterKeyUsage,
} from "./adminOpenRouterKeyUsage.core.js";
export {
  type AdminOpenRouterKeyUsageQueryData,
  buildAdminOpenRouterKeyUsageQuery,
  prefetchAdminOpenRouterKeyUsage,
  queryKeyAdminOpenRouterKeyUsage,
};

export type AdminOpenRouterKeyUsageQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * getKeyUsage adminOpenRouterKeys
 *
 * @remarks
 * Fetch an organization's live credit usage from OpenRouter for one key. Requires platform admin.
 */
export function useAdminOpenRouterKeyUsage(
  request: GetAdminOpenRouterKeyUsageRequest,
  security?: GetAdminOpenRouterKeyUsageSecurity | undefined,
  options?: QueryHookOptions<
    AdminOpenRouterKeyUsageQueryData,
    AdminOpenRouterKeyUsageQueryError
  >,
): UseQueryResult<
  AdminOpenRouterKeyUsageQueryData,
  AdminOpenRouterKeyUsageQueryError
> {
  const client = useGramContext();
  return useQuery({
    ...buildAdminOpenRouterKeyUsageQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * getKeyUsage adminOpenRouterKeys
 *
 * @remarks
 * Fetch an organization's live credit usage from OpenRouter for one key. Requires platform admin.
 */
export function useAdminOpenRouterKeyUsageSuspense(
  request: GetAdminOpenRouterKeyUsageRequest,
  security?: GetAdminOpenRouterKeyUsageSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    AdminOpenRouterKeyUsageQueryData,
    AdminOpenRouterKeyUsageQueryError
  >,
): UseSuspenseQueryResult<
  AdminOpenRouterKeyUsageQueryData,
  AdminOpenRouterKeyUsageQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildAdminOpenRouterKeyUsageQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setAdminOpenRouterKeyUsageData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      organizationId: string;
      keyType: KeyType;
      gramSession?: string | undefined;
    },
  ],
  data: AdminOpenRouterKeyUsageQueryData,
): AdminOpenRouterKeyUsageQueryData | undefined {
  const key = queryKeyAdminOpenRouterKeyUsage(...queryKeyBase);

  return client.setQueryData<AdminOpenRouterKeyUsageQueryData>(key, data);
}

export function invalidateAdminOpenRouterKeyUsage(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      organizationId: string;
      keyType: KeyType;
      gramSession?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "adminOpenRouterKeys",
      "getKeyUsage",
      ...queryKeyBase,
    ],
  });
}

export function invalidateAllAdminOpenRouterKeyUsage(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "adminOpenRouterKeys", "getKeyUsage"],
  });
}
